import { Fragment, type ReactNode } from 'react'
import { usePublicHref } from '../public/context'

/**
 * Rendu du texte « riche » : **gras**, *italique*, [lien](url), et des
 * paragraphes séparés par une ligne vide. Jamais de HTML : React échappe
 * tout, seul le `href` est filtré (voir `safeHref`).
 */

export function safeHref(href: string): string {
  const v = href.trim()
  return /^(\/(?!\/)|#|https?:\/\/|mailto:|tel:)/i.test(v) ? v : '#'
}

const INLINE = /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)\s]+\))/g

function Inline({ text }: { text: string }) {
  const href = usePublicHref()
  const parts = text.split(INLINE)
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4)
          return <strong key={i}>{part.slice(2, -2)}</strong>
        if (part.startsWith('*') && part.endsWith('*') && part.length > 2)
          return <em key={i}>{part.slice(1, -1)}</em>
        const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part)
        if (link) {
          const url = href(safeHref(link[2]!))
          const external = /^https?:/.test(url)
          return (
            <a
              key={i}
              href={url}
              {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
              {link[1]}
            </a>
          )
        }
        // Retours à la ligne simples conservés.
        return (
          <Fragment key={i}>
            {part.split('\n').map((line, j) => (
              <Fragment key={j}>
                {j > 0 && <br />}
                {line}
              </Fragment>
            ))}
          </Fragment>
        )
      })}
    </>
  )
}

export function RichText({ text, className }: { text: string; className?: string }): ReactNode {
  const paragraphs = text.split(/\n\s*\n/).filter((p) => p.trim())
  return (
    <div className={className}>
      {paragraphs.map((p, i) => (
        <p key={i}>
          <Inline text={p.trim()} />
        </p>
      ))}
    </div>
  )
}

export function InlineText({ text }: { text: string }) {
  return <Inline text={text} />
}

export function lines(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
}

export function formatDate(iso: string | null, withTime = false): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  })
}
